import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, Divider, FormControl, FormControlLabel, Grid, InputAdornment, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import { useContext, useMemo, useState } from 'react';
import { changeStatus, fetchContractInfo, fetchContracts, fetchLeadsCap, fetchOfferInfo, updateLeadsCap, setSchedulePrice, fetchSchedule, updateRevenueModel } from '../../utils/leadspedia/api';
import { UserContext } from '../../contexts/user.context';
import { sendEmail } from '../../utils/emailjs/emailjs.utils';
import { set } from 'react-hook-form';

// Define the props interface for BundleOffer
interface BundleOfferProps {
    campaignType: string;
    contractId: number;
    verticalId: string;
    offerId: string;
    type: string;
}

interface OfferInfo {
    offerID: string;
    offerName: string;
    mode: string;
    status: string;
    approval: string;
    alternativeID: string;
    offerDescription: string;
    testingInstructions: string;
    offerRestrictions: string;
    offerExpirationDate: string;
    previewURL: string;
    notes: string;
}

interface ContractInfo {
    contractID: string;
    buyerID: string;
    verticalID: string;
    status: string;
    mode: string;
    transferPhoneNumber: string;
    transferPrice: string;
    transferRevenueModel: string;
    transferCallDuration: string;
    contractType: string;
    contractName: string;
    alternativeID: string;
    expirationDate: string;
    notes: string;
    createdOn: string;
    buyerName: string;
    verticalName: string;
}



const BundleOffer: React.FC<BundleOfferProps> = ({ campaignType, contractId, verticalId, offerId, type }) => {

    const { currentUser } = useContext(UserContext);

    const [checked, setChecked] = useState<boolean>(true);
    const [switchDisabled, setSwitchDisabled] = useState<boolean>(false);
    const [offerInfo, setOfferInfo] = useState<OfferInfo | null>(null);
    const [contractInfo, setContractInfo] = useState<ContractInfo | null>(null);
    const [schedule, setSchedule] = useState<any[]>([]);
    const [cap, setCap] = useState<any | undefined>(undefined);
    const [capSaved, setCapSaved] = useState(false);
    const [price, setPrice] = useState('');
    const [priceSaved, setPriceSaved] = useState(false);
    const [lastPrice, setLastPrice] = useState(''); // to track last price for email notification
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getOfferInfo = async () => {
        setIsSubmitting(true);
        setSwitchDisabled(true);
        try {
            const data = await fetchOfferInfo(Number(offerId));
            if (data) {
                setOfferInfo(data);
                fetchContractInfo(contractId, campaignType).then(contractData => {
                    if (contractData) {
                        setContractInfo(contractData);
                        setChecked(contractData.status === 'Active');
                        if (type === 'leads') {
                            fetchAndSetScheduledPrice(contractId);
                        } else {
                            setPrice(contractData.transferPrice);
                            setLastPrice(contractData.transferPrice);
                        }
                    }
                });  
                setIsSubmitting(false);
                setSwitchDisabled(false);
            }
        } catch (error) {
            console.error('Error fetching offer info:', error);
        }
    };

    const getCap = async () => {
        const capData = await fetchLeadsCap(contractId);
        if (capData) {
            setCap(capData.weeklyLeadsCap);
        }
    }

    const fetchAndSetScheduledPrice = async (campaignContractId: number) => {
        if (campaignContractId === 0) return;
        try {
            let price = '';
            const schedule = await fetchSchedule(campaignContractId);
            if (schedule && schedule.length > 0) {
                price = schedule[0].price;
                setSchedule(schedule);
            }
            setPrice(price);
            setLastPrice(price);

        } catch (error: any) {
            console.error('Error fetching data:', error.message);
        }
    }


    const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const campaignToggle = event.target.checked;
        setChecked(campaignToggle);
        setSwitchDisabled(true);

        // Chnage the status of the contract to active/inactive
        changeStatus(contractId, campaignToggle ? 'Active' : 'Paused', campaignType)
            .then(() => {
                setSwitchDisabled(false);
            })
            .catch(() => {
                // Revert the toggle state on error
                setChecked(!campaignToggle);
                setSwitchDisabled(false);
            });
    }

    const handleSubmitBid = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsSubmitting(true);

        if (price === lastPrice || price === '' || parseFloat(price) <= 0) {
            // No change in price
            setIsSubmitting(false);
            return;
        }
        var updatePriceResult = null;
        if (type === 'leads') {
            updatePriceResult = await setSchedulePrice(contractId, parseFloat(price));
        } else {
            if (contractInfo !== null) {
                updatePriceResult = await updateRevenueModel(contractId, contractInfo.transferRevenueModel, contractInfo.transferCallDuration, parseFloat(price));
            }
        }

        if (updatePriceResult?.success) {
            bidSubmitted();
        }
    }

    const handleSubmitCap = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            const updateCapResponse = await updateLeadsCap(contractId, parseInt(cap));
            if (updateCapResponse) {
                setCapSaved(true);
                setTimeout(() => setCapSaved(false), 3000);
            }
        } catch (error) {
            console.error('Error submitting cap:', error);
        } finally {
            setIsSubmitting(false);
        }
    }

    function bidSubmitted() {
        // successful price submission
        console.log('Price submitted successfully');
        setPriceSaved(true);
        sendBidChangeEmail();
        setTimeout(() => {
            setPriceSaved(false);
            setLastPrice(price); // update last price
        }, 3000);
        setIsSubmitting(false);
    }

    function sendBidChangeEmail() {
        if (currentUser && currentUser !== null) {
            sendEmail('Advertiser ' + currentUser.displayName + ', ID: ' + currentUser.advertiserId + ' has changed bid for bundeld offer: ' + offerInfo?.offerName + ' offerID: ' + offerId + ' - from $' + lastPrice + ' to $' + price);
        }
    }


    useMemo(() => {
        getOfferInfo();
        getCap();
    }, [offerId, contractId]);


    function canBid(): boolean {
        if (type === 'leads') {
            return schedule.length > 0;
        }
        return contractInfo?.transferPrice !== '';
    }

    return (
        <Box sx={{ flexGrow: 1, width: '100%', padding: 2, boxSizing: 'border-box', minWidth: '600px' }}>
            <Grid container sx={{ padding: 2, justifyContent: 'center' }} spacing={2}>
                <Grid item xs={12}>
                    <Card className={`campaignCard ${!checked ? 'inactive' : ''}`}>
                        <CardHeader
                            className='cardHeader'
                            avatar={
                                <Avatar className='campaignLogo'>
                                    OF{offerId}
                                </Avatar>
                            }
                            action={
                                <Tooltip title="Toggle leads/calls for this offer">
                                    <FormControlLabel
                                        sx={{ mr: '4px', mt: '4px' }}
                                        value={checked}
                                        control={<Switch disabled={switchDisabled} checked={checked} onChange={handleToggle} color="primary" />}
                                        label=""
                                        labelPlacement="start"
                                        name='active'
                                    />
                                </Tooltip>
                            }
                            title={
                                <Typography sx={{ fontWeight: '600' }} variant='subtitle1'>
                                    {offerInfo?.offerName || 'Offer Name'}
                                </Typography>
                            }
                            subheader={
                                <div className='cardSubheader'>
                                    <span>Bundled Offer</span>
                                </div>
                            }
                        />
                        <Divider />
                        <CardContent className='cardContent'>
                            <div>
                                <Typography sx={{ fontSize: '0.8rem' }}>
                                    {offerInfo?.notes || 'Info available soon'}
                                </Typography>
                            </div>
                        </CardContent>

                        <CardActions disableSpacing>
                            <Box sx={{ p: '8px', width: '100%' }} >
                                {contractId !== 0 ? (
                                    <Stack direction="row" spacing={2}>
                                        <FormControl variant="standard" className='bidInput'>
                                            <TextField
                                                id={"bid-" + offerId.toString()}
                                                value={price}
                                                type="number"
                                                variant="standard"
                                                sx={{ pb: '16px' }}
                                                onChange={(e) => setPrice(e.target.value)}
                                                label="My Current Bid"
                                                disabled={isSubmitting || !canBid()}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    inputProps: {
                                                        min: 0,
                                                        onKeyDown: (e) => {
                                                            // Prevent entering '-', '+', or 'e'
                                                            if (["-", "+", "e"].includes(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        },
                                                    },
                                                }}
                                            />
                                            {priceSaved ? (
                                                <Button
                                                    variant="contained" endIcon={<CheckCircleIcon />}
                                                    size="small"
                                                    color="success"
                                                    title='Saved'
                                                >
                                                    Saved!
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained" endIcon={<SaveIcon />}
                                                    size="small"
                                                    onClick={handleSubmitBid}
                                                    color="primary"
                                                    title='Save bid amount'
                                                    disabled={isSubmitting || !canBid() || price === ''}
                                                >
                                                    Save
                                                </Button>
                                            )
                                            }
                                        </FormControl>
                                        {campaignType === 'leads' ? (
                                            <FormControl variant="standard" className='bidInput'>
                                                <TextField
                                                    id={"cap-" + offerInfo?.offerID.toString()}
                                                    value={cap}
                                                    type="number"
                                                    variant="standard"
                                                    sx={{ pb: '16px' }}
                                                    onChange={(e) => setCap(e.target.value)}
                                                    label="Weekly Cap"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">#</InputAdornment>,
                                                        inputProps: {
                                                            min: 1,
                                                            onKeyDown: (e) => {
                                                                // Prevent entering '-', '+', or 'e'
                                                                if (["-", "+", "e"].includes(e.key)) {
                                                                    e.preventDefault();
                                                                }
                                                            },
                                                        }
                                                    }}
                                                />
                                                {capSaved ? (
                                                    <Button
                                                        variant="contained" endIcon={<CheckCircleIcon />}
                                                        size="small"
                                                        color="success"
                                                        title='Saved'
                                                    >
                                                        Saved!
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="contained" endIcon={<SaveIcon />}
                                                        size="small"
                                                        onClick={handleSubmitCap}
                                                        color="primary"
                                                        title='Save cap amount'
                                                        disabled={isSubmitting}
                                                    >
                                                        Save
                                                    </Button>
                                                )
                                                }
                                            </FormControl>
                                        ) : null}
                                    </Stack>
                                ) : null
                                }
                                {/* <Box>
                                    <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{ textAlign: 'center', paddingTop: '10px' }}
                                    >
                                        Avg.bid: <strong>{scheduledPrice}</strong>
                                    </Typography>
                                </Box> */}
                            </Box>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default BundleOffer;